import { useState, useEffect, useCallback } from 'react';
import UpdateFournisseurForm from "../../components/update/update-fournisseur-form"; // Adjust path as needed based on your project structure
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading'; 

export default function Page() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const { id } = useParams(); 
  const [fournisseur, setFournisseur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFournisseur = useCallback(async () => {
    if (!id) {
      setError(t('fournisseurs.id_missing')); 
      setLoading(false);
      return;
    }

    setLoading(true); // Start loading
    setError(null);    // Clear any previous errors
    try {
      if (!token) {
        toast.error(t('fournisseurs.auth_required'));
        setError(t('errors.auth_token_missing'));
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/fournisseurs/${id}`, { // Fetch specific fournisseur by ID
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`, // Include the authorization token
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `${t('fournisseurs.fetch_failed')}: ${res.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await res.json();
      // Assuming your API returns data in a { status: true, data: { ...fournisseur_object } } format
      if (data.status && data.data) {
        setFournisseur(data.data); // Set the fetched supplier data
      } else {
        throw new Error(data.message || t('errors.unexpected_format'));
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du fournisseur :", err);
      setError(err.message || t('fournisseurs.fetch_error'));
      toast.error(`${t('messages.error')}: ${err.message || t('fournisseurs.load_failed')}`);
    } finally {
      setLoading(false); // End loading regardless of success or failure
    }
  }, [id]); // Dependency on 'id' ensures re-fetch if ID changes

  // useEffect to call the fetch function when the component mounts or fetchFournisseur changes
  useEffect(() => {
    fetchFournisseur();
  }, [fetchFournisseur]);

  // Conditional rendering based on loading, error, and data presence
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading /> {/* Display loading spinner */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-2xl font-bold text-red-700 mb-4">{t('fournisseurs.error_loading')}</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchFournisseur} // Allow retrying the fetch
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!fournisseur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-700 mb-4">{t('fournisseurs.not_found_id')}</h2>
          <p className="text-gray-600 text-xs">{t('fournisseurs.not_found_id_desc').replace('{id}', id)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            {t('fournisseurs.update_title')}
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            {t('fournisseurs.update_subtitle')}
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-2xl mx-auto">
          {/* Pass the fetched fournisseur data to the UpdateFournisseurForm */}
          <UpdateFournisseurForm fournisseur={fournisseur} />
        </div>
      </div>
    </div>
  );
}
